import { useEffect, useMemo, useState } from 'react';
import { ArrowDownIcon, ArrowLeftIcon, ArrowUpIcon, PlusIcon, TrashIcon } from 'lucide-react';
import { QuillEditor, deltaToPlainText, type QuillDelta } from '@/components/custom/quill-editor';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { AssetPicker } from '@/components/shared/asset-picker';
import { PageHeader, PageSection } from '@/components/layouts/page-layout';
import type { CollectionColumn } from '@/types/entities';
import { parseArrayValue, parseRichTextValue, isEmptyValue } from '@/lib/collection-field-utils';

type EditorMode = 'create' | 'edit';

type RecordEditorPageProps = {
  mode: EditorMode;
  resourceName: string;
  columns: CollectionColumn[];
  tenantSlug: string;
  initialData?: Record<string, unknown>;
  onSave: (formData: Record<string, unknown>) => Promise<void>;
  onBack: () => void;
  isSaving?: boolean;
  submitLabel?: string;
};

const hasRichTextContent = (delta: QuillDelta): boolean => {
  const text = deltaToPlainText(delta);
  return text.trim().length > 0 || delta.ops?.some(op => 'insert' in op && typeof op.insert === 'object');
};

const buildInitialData = (columns: CollectionColumn[], initialData?: Record<string, unknown>) => {
  return columns.reduce((acc, col) => {
    if (col.type === 'richtext') {
      acc[col.name] = parseRichTextValue(initialData?.[col.name] ?? col.default);
      return acc;
    }

    if (col.type === 'image') {
      acc[col.name] = initialData?.[col.name] ?? col.default ?? null;
      return acc;
    }

    if (col.type === 'array') {
      acc[col.name] = parseArrayValue(initialData?.[col.name] ?? col.default);
      return acc;
    }

    if (col.type === 'boolean') {
      const rawValue = initialData?.[col.name] ?? col.default;
      acc[col.name] =
        typeof rawValue === 'boolean'
          ? rawValue
          : rawValue === 1 || rawValue === '1' || rawValue === 'true';
      return acc;
    }

    acc[col.name] = initialData?.[col.name] ?? col.default ?? '';
    return acc;
  }, {} as Record<string, unknown>);
};

export function RecordEditorPage({
  mode,
  resourceName,
  columns,
  tenantSlug,
  initialData,
  onSave,
  onBack,
  isSaving = false,
  submitLabel,
}: RecordEditorPageProps) {
  const [initialFormData] = useState<Record<string, unknown>>(() => buildInitialData(columns, initialData));
  const [formData, setFormData] = useState<Record<string, unknown>>(initialFormData);
  const [error, setError] = useState<string | null>(null);

  const isDirty = useMemo(() => {
    return JSON.stringify(formData) !== JSON.stringify(initialFormData);
  }, [formData, initialFormData]);

  useEffect(() => {
    if (!isDirty) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isDirty]);

  const handleBack = () => {
    if (isDirty && !window.confirm('You have unsaved changes. Leave this page?')) {
      return;
    }
    onBack();
  };

  const handleSave = async () => {
    try {
      for (const col of columns) {
        const value = formData[col.name];

        if (!col.required) {
          continue;
        }

        if (col.type === 'richtext') {
          const deltaValue = parseRichTextValue(value);
          if (!hasRichTextContent(deltaValue)) {
            setError(`${col.label} is required`);
            return;
          }
          continue;
        }

        if (isEmptyValue(value)) {
          setError(`${col.label} is required`);
          return;
        }
      }

      await onSave(formData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save data');
    }
  };

  const renderField = (column: CollectionColumn) => {
    const value = formData[column.name];

    if (column.type === 'array') {
      const rows = parseArrayValue(value);
      const itemFields = column.items?.fields ?? [];

      const updateRows = (nextRows: Record<string, unknown>[]) => {
        setFormData({
          ...formData,
          [column.name]: nextRows,
        });
      };

      const addRow = () => {
        const emptyRow = itemFields.reduce((acc, itemField) => {
          if (itemField.type === 'image') {
            acc[itemField.name] = null;
          } else if (itemField.type === 'richtext') {
            acc[itemField.name] = parseRichTextValue(undefined);
          } else {
            acc[itemField.name] = '';
          }
          return acc;
        }, {} as Record<string, unknown>);
        updateRows([...rows, emptyRow]);
      };

      const updateRowValue = (rowIndex: number, fieldName: string, nextValue: unknown) => {
        const nextRows = rows.map((row: Record<string, unknown>, index: number) => {
          if (index !== rowIndex) {
            return row;
          }
          return {
            ...row,
            [fieldName]: nextValue,
          };
        });
        updateRows(nextRows);
      };

      const removeRow = (rowIndex: number) => {
        updateRows(rows.filter((_: Record<string, unknown>, index: number) => index !== rowIndex));
      };

      const moveRow = (rowIndex: number, direction: -1 | 1) => {
        const targetIndex = rowIndex + direction;
        if (targetIndex < 0 || targetIndex >= rows.length) {
          return;
        }

        const nextRows = [...rows];
        const [movedRow] = nextRows.splice(rowIndex, 1);
        nextRows.splice(targetIndex, 0, movedRow);
        updateRows(nextRows);
      };

      return (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">{rows.length} {rows.length === 1 ? 'row' : 'rows'}</p>
            <Button type="button" variant="outline" size="sm" className="gap-1" onClick={addRow}>
              <PlusIcon className="size-3" />
              Add Row
            </Button>
          </div>

          {itemFields.length === 0 ? (
            <div className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
              This array column has no item fields configured.
            </div>
          ) : rows.length === 0 ? (
            <div className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
              No rows yet. Click Add Row.
            </div>
          ) : (
            <div className="space-y-2">
              {rows.map((row: Record<string, unknown>, rowIndex: number) => (
                <div key={rowIndex} className="rounded-md border p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <p className="text-xs font-medium text-muted-foreground">Row {rowIndex + 1}</p>
                    <div className="flex items-center gap-1">
                      <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => moveRow(rowIndex, -1)} disabled={rowIndex === 0}>
                        <ArrowUpIcon className="size-3" />
                      </Button>
                      <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => moveRow(rowIndex, 1)} disabled={rowIndex === rows.length - 1}>
                        <ArrowDownIcon className="size-3" />
                      </Button>
                      <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => removeRow(rowIndex)}>
                        <TrashIcon className="size-3" />
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {itemFields.map((itemField) => {
                      const itemValue = row[itemField.name];
                      return (
                        <div key={itemField.name} className="grid gap-1">
                          <Label className="text-xs" htmlFor={`${column.name}-${rowIndex}-${itemField.name}`}>
                            {itemField.label}
                            {itemField.required && <span className="ml-1 text-destructive">*</span>}
                          </Label>

                          {itemField.type === 'image' ? (
                            <AssetPicker
                              tenantSlug={tenantSlug}
                              value={typeof itemValue === 'number' ? itemValue : itemValue ? Number(itemValue) : null}
                              onChange={(nextValue) => updateRowValue(rowIndex, itemField.name, nextValue)}
                            />
                          ) : itemField.type === 'richtext' ? (
                            <QuillEditor
                              value={parseRichTextValue(itemValue)}
                              onChange={(nextDelta) => updateRowValue(rowIndex, itemField.name, nextDelta)}
                              tenantSlug={tenantSlug}
                              placeholder="Write rich content..."
                            />
                          ) : (
                            <Input
                              id={`${column.name}-${rowIndex}-${itemField.name}`}
                              type={itemField.type === 'datetime' ? 'datetime-local' : 'text'}
                              value={String(itemValue ?? '')}
                              onChange={(e) => updateRowValue(rowIndex, itemField.name, e.target.value)}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (column.type === 'boolean') {
      return (
        <Checkbox
          id={column.name}
          checked={!!value}
          onCheckedChange={(checked) =>
            setFormData({
              ...formData,
              [column.name]: checked,
            })
          }
        />
      );
    }

    if (column.type === 'richtext') {
      const deltaValue = parseRichTextValue(value);
      return (
        <div className="space-y-3">
          <QuillEditor
            value={deltaValue}
            onChange={(nextDelta) =>
              setFormData({
                ...formData,
                [column.name]: nextDelta,
              })
            }
            tenantSlug={tenantSlug}
            placeholder="Write rich content..."
          />
        </div>
      );
    }

    if (column.type === 'image') {
      return (
        <AssetPicker
          tenantSlug={tenantSlug}
          value={typeof value === 'number' ? value : value ? Number(value) : null}
          onChange={(nextValue) =>
            setFormData({
              ...formData,
              [column.name]: nextValue,
            })
          }
        />
      );
    }

    let inputType = 'text';
    if (column.type === 'integer' || column.type === 'double') {
      inputType = 'number';
    } else if (column.type === 'datetime') {
      inputType = 'datetime-local';
    }

    return (
      <Input
        id={column.name}
        type={inputType}
        value={String(value ?? '')}
        onChange={(e) =>
          setFormData({
            ...formData,
            [column.name]:
              column.type === 'integer' || column.type === 'double'
                ? parseFloat(e.target.value) || ''
                : e.target.value,
          })
        }
      />
    );
  };

  const title = mode === 'edit' ? `Edit ${resourceName}` : `Create ${resourceName}`;

  return (
    <div className="flex h-full flex-col">
      <PageSection className="border-b py-4">
        <div className="flex items-center gap-3">
          <Button type="button" variant="outline" size="icon" onClick={handleBack} aria-label="Back" title="Back">
            <ArrowLeftIcon className="size-4" />
          </Button>
          <PageHeader title={title} className="flex-1 gap-0 md:justify-start md:items-center" />
        </div>
      </PageSection>

      {error && (
        <div className="bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="flex-1 overflow-y-auto px-4 py-6">
        <div className="max-w-4xl space-y-4">
          {columns.map((column) => (
            <div key={column.name} className="grid gap-2">
              <Label htmlFor={column.name}>
                {column.label}
                {column.required && <span className="ml-1 text-destructive">*</span>}
              </Label>
              {renderField(column)}
            </div>
          ))}
        </div>
      </div>

      <div className="border-t px-4 py-4">
        <div className="flex items-center justify-end gap-2">
          <Button type="button" variant="outline" onClick={handleBack} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : submitLabel ?? 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
}
